import { FOLDER_AUDIO_RECEIPT_KEY } from './folder-audio-receipt';
import { canonicalJson } from '../quote-engine/canonical-json';
import { prepareProjectEvidence, getProjectEvidencePreparationFingerprint } from './project-evidence-preparation';
import { projectAuthoritativeInputProjection } from './project-intake-persistence';
import { captureProjectRunContext, createStaleProjectRunError, isProjectRunContextCurrent } from '../utils/project-run-context';

const EVIDENCE_LANES = ['documents', 'photos', 'videos', 'videoTranscripts', 'preprocessedFiles'] as const;

/** Runtime-only markers and display locators are never operator-authored evidence. */
function withoutRuntimeOnlyFields(value: unknown): unknown {
    if (Array.isArray(value)) return value.map(withoutRuntimeOnlyFields);
    if (!value || typeof value !== 'object') return value;
    const out: Record<string, unknown> = {};
    for (const [key, inner] of Object.entries(value as Record<string, unknown>)) {
        if (key.startsWith('_') || key === 'url') continue;
        out[key] = withoutRuntimeOnlyFields(inner);
    }
    return out;
}

/**
 * What an operator can actually change about the evidence, per lane.
 *
 * After a project loads, the app itself replaces these arrays several times
 * without changing their meaning: photos gain cloud keys, document and video
 * rows gain preview URLs, unowned blob locators are stripped. Reuses the
 * projection that already decides whether two copies of a project carry the
 * same builder inputs, so transport fields are defined in exactly one place.
 */
function editableEvidenceLanes(evidence: Record<string, unknown>): Record<string, unknown[]> {
    const projection = projectAuthoritativeInputProjection(evidence) as Record<string, unknown>;
    const lanes: Record<string, unknown[]> = {};
    for (const lane of EVIDENCE_LANES) {
        const rows = withoutRuntimeOnlyFields(projection[lane] ?? []);
        lanes[lane] = Array.isArray(rows) ? rows : [];
    }
    return lanes;
}

export function evidenceEditSignature(evidence: Record<string, unknown>): Record<string, string> {
    const lanes = editableEvidenceLanes(evidence);
    const signature: Record<string, string> = {};
    for (const lane of EVIDENCE_LANES) signature[lane] = JSON.stringify(lanes[lane]);
    return signature;
}

/**
 * Field NAMES that differ in the given lanes, for the stopped job's diagnostics.
 * Names only, never values: evidence rows hold customer document text.
 */
export function evidenceEditedFields(
    before: Record<string, unknown>, after: Record<string, unknown>, lanes: readonly string[],
): string[] {
    const beforeLanes = editableEvidenceLanes(before);
    const afterLanes = editableEvidenceLanes(after);
    const fields = new Set<string>();
    for (const lane of lanes) {
        const prior = beforeLanes[lane] || [];
        const next = afterLanes[lane] || [];
        if (prior.length !== next.length) fields.add(`${lane}.length(${prior.length}->${next.length})`);
        const rows = Math.min(prior.length, next.length);
        for (let index = 0; index < rows; index += 1) {
            const left = (prior[index] && typeof prior[index] === 'object' ? prior[index] : {}) as Record<string, unknown>;
            const right = (next[index] && typeof next[index] === 'object' ? next[index] : {}) as Record<string, unknown>;
            for (const key of new Set([...Object.keys(left), ...Object.keys(right)])) {
                if (JSON.stringify(left[key]) !== JSON.stringify(right[key])) fields.add(`${lane}[].${key}`);
            }
        }
    }
    return [...fields].sort().slice(0, 20);
}

/** App-owned persistence boundary shared by attended Summary and Quote Run. */
export async function runPrepareStage1Evidence(options: {
    expectedProjectId?: string | null;
    selectedFileIds?: string[] | null;
    signal?: AbortSignal | null;
    /** An attended run sets unreadable PDFs and videos aside instead of stopping. */
    setAsideUnreadableFiles?: boolean;
} = {}, deps: {
    getContext: () => { projectId: string | null; loadGeneration: number; signal: AbortSignal | null };
    getLiveEvidence: () => Record<string, unknown>;
    getLiveForm: () => Record<string, any>;
    loadProject: (id: string) => Promise<any>;
    saveProject: (patch: Record<string, unknown>, options: any) => Promise<any>;
    adoptEvidence: (project: any, options: { formDataChanged: boolean }) => void;
    apiKey: string;
    model: string;
    audioModel: string;
}) {
    const context = captureProjectRunContext(deps.getContext());
    const projectId = options.expectedProjectId ?? context.projectId;
    const assertCurrent = () => {
        if (!projectId || projectId !== context.projectId || options.signal?.aborted
            || !isProjectRunContextCurrent(context, deps.getContext())) {
            throw createStaleProjectRunError('Preparing project evidence');
        }
    };
    assertCurrent();
    let liveEvidence = deps.getLiveEvidence();
    let project = await deps.loadProject(projectId!);
    assertCurrent();
    if (!project || project.id !== projectId) {
        throw Object.assign(new Error('Save this project before preparing its evidence.'), { code: 'PROJECT_NOT_LOCAL', retryable: true });
    }
    const signals = [context.signal, options.signal].filter(Boolean) as AbortSignal[];
    const signal = signals.length > 1 ? AbortSignal.any(signals) : signals[0];
    // Captured eagerly: the baseline must describe the arrays as they were when
    // preparation started, not as they are when a later repaint is noticed.
    let liveSignature = evidenceEditSignature(liveEvidence);
    const assertUnchanged = () => {
        assertCurrent();
        const current = deps.getLiveEvidence();
        // Fast path. Array identity is a cheap proxy that is almost always
        // enough, and it keeps the per-file checks free.
        if (!Object.keys(liveEvidence).some(key => current[key] !== liveEvidence[key])) return;
        // Identity alone cannot tell an operator's edit from the app's own
        // post-load hydration. completeProjectLoad starts un-awaited passes
        // (cloud photo repair, document preview rehydrate, blob-ownership
        // strips) and Stage 1's video rows gain playback URLs. Every one of
        // them replaces an array without changing the evidence. Treating that
        // as an edit stopped EVERY freshly imported project within a second of
        // Stage 1 starting, because a fresh import always needs those passes.
        const currentSignature = evidenceEditSignature(current);
        const editedLanes = EVIDENCE_LANES.filter(lane => currentSignature[lane] !== liveSignature[lane]);
        if (editedLanes.length > 0) {
            throw Object.assign(createStaleProjectRunError('Project evidence was edited during preparation'), {
                details: {
                    changedLanes: [...editedLanes],
                    changedFields: evidenceEditedFields(liveEvidence, current, editedLanes),
                },
            });
        }
        // Same evidence, new arrays: adopt them so the fast path resumes.
        liveEvidence = current;
        liveSignature = currentSignature;
    };
    let savedAny = false;
    const checkpoint = async (result: { project: any; changed: boolean }) => {
        assertUnchanged();
        if (!result.changed) return project;
        const prepared = result.project;
        const liveForm = deps.getLiveForm();
        const oldNotes = String(project.formData?.notes || '');
        const preparedNotes = String(prepared.formData?.notes || '');
        if (!preparedNotes.startsWith(oldNotes)) {
            throw new Error('Evidence preparation must preserve existing inspector notes.');
        }
        const appendedNotes = preparedNotes.slice(oldNotes.length);
        // Only evidence and a newly extracted transcript belong to this write.
        // Rebase that appendix on current manual notes, never a stale form.
        // Write only the lanes this preparation step actually produced. `project`
        // is a record snapshot taken when preparation started, and the app's own
        // load repairs (cloud photo keys, document previews) can land after it.
        // Sending an untouched lane back from that snapshot would overwrite the
        // newer repaired rows. Both preparation steps build their result as
        // `{ ...project, <changed lanes> }`, so an untouched lane keeps its
        // reference and the save falls through to the live state for it.
        const changedLane = (lane: typeof EVIDENCE_LANES[number]) => (
            prepared[lane] !== project[lane] ? { [lane]: prepared[lane] || [] } : {}
        );
        // One exception, for one field family. A video's rows here come from the
        // snapshot, and transcribing a clip takes minutes. Meanwhile the
        // background proxy upload (video-cloud-upload.ts) can finish and stamp
        // cloudMedia, cloudPlayback and cloudProxy* onto the LIVE row only. The
        // save now writes these rows as given (it must, or prepared evidence is
        // dropped), and adoptEvidence then replaces live state with them, so the
        // pointer vanished from both. The uploaded derivative was left
        // unreferenced and the clip was transcoded and stored a second time.
        // Preparation never writes this family, so the live value simply wins.
        // Nothing else is taken from the live row: it can carry an
        // ownership-stripped locator that must not be persisted.
        const isCloudProxyState = (key: string) => key === 'cloudMedia' || key === 'cloudPlayback' || key.startsWith('cloudProxy');
        const withLiveCloudProxyState = (rows: any[]) => {
            const liveRows = liveEvidence.videos;
            if (!Array.isArray(liveRows)) return rows;
            return rows.map((row: any) => {
                const live = row?.id == null ? null : liveRows.find((entry: any) => entry?.id === row.id);
                if (!live) return row;
                const proxyState = Object.fromEntries(Object.entries(live).filter(([key]) => isCloudProxyState(key)));
                return Object.keys(proxyState).length > 0 ? { ...row, ...proxyState } : row;
            });
        };
        const changedVideos = changedLane('videos');
        // A folder recording has no row (a Stage 1 drop keeps none), so its
        // record on the form is its only trace: what was added, what was set
        // aside. It rides the same form write as the notes, even when a
        // recording set aside appended none.
        // Compared by content, not key order: the readback is the server's copy,
        // and Postgres jsonb stores an object's keys in its own order
        // ({ version, sources } comes back as { sources, version }).
        const sameRecord = (a: unknown, b: unknown) => canonicalJson(JSON.parse(JSON.stringify(a ?? null)))
            === canonicalJson(JSON.parse(JSON.stringify(b ?? null)));
        const preparedAudioReceipt = prepared.formData?.[FOLDER_AUDIO_RECEIPT_KEY];
        const audioReceiptChanged = preparedAudioReceipt !== undefined
            && !sameRecord(preparedAudioReceipt, project.formData?.[FOLDER_AUDIO_RECEIPT_KEY]);
        const formChanged = Boolean(appendedNotes) || audioReceiptChanged;
        const patch: Record<string, any> = {
            ...changedLane('documents'), ...changedLane('photos'),
            ...(changedVideos.videos ? { videos: withLiveCloudProxyState(changedVideos.videos) } : {}),
            ...changedLane('videoTranscripts'),
            ...changedLane('preprocessedFiles'),
            ...(formChanged ? { formData: {
                ...liveForm, notes: String(liveForm.notes || '') + appendedNotes,
                ...(audioReceiptChanged ? { [FOLDER_AUDIO_RECEIPT_KEY]: preparedAudioReceipt } : {}),
            } } : {}),
        };
        const beforeCommit = () => {
            assertUnchanged();
            if (formChanged && deps.getLiveForm() !== liveForm) {
                throw createStaleProjectRunError('Inspector notes changed during evidence save');
            }
        };
        await deps.saveProject(patch, { runContext: context, beforeCommit, deferMediaStateAdoption: true });
        beforeCommit();
        const verified = await deps.loadProject(projectId!);
        beforeCommit();
        if (!verified || verified.id !== projectId
            || (appendedNotes && verified.formData?.notes !== patch.formData?.notes)
            || (audioReceiptChanged && !sameRecord(verified.formData?.[FOLDER_AUDIO_RECEIPT_KEY], preparedAudioReceipt))
            || getProjectEvidencePreparationFingerprint(verified) !== getProjectEvidencePreparationFingerprint(prepared)) {
            throw Object.assign(new Error('Prepared evidence did not survive the project save. Retry after restoring the file service.'), {
                code: 'PROJECT_EVIDENCE_SAVE_UNVERIFIED', retryable: true,
            });
        }
        deps.adoptEvidence(verified, { formDataChanged: formChanged });
        liveEvidence = deps.getLiveEvidence();
        liveSignature = evidenceEditSignature(liveEvidence);
        project = verified;
        savedAny = true;
        return verified;
    };
    assertUnchanged();
    const result = await prepareProjectEvidence({
        project, selectedFileIds: options.selectedFileIds ?? null,
        apiKey: deps.apiKey, model: deps.model, audioModel: deps.audioModel,
        signal, assertCurrent: assertUnchanged, onEvidencePrepared: checkpoint,
        setAsideUnreadableFiles: options.setAsideUnreadableFiles === true,
    });
    assertUnchanged();
    if (result.changed && getProjectEvidencePreparationFingerprint(result.project) !== getProjectEvidencePreparationFingerprint(project)) {
        await checkpoint(result);
    }
    return { ok: true, changed: savedAny, project };
}
